/* eslint-disable react-hooks/exhaustive-deps */
import { tokenContract } from '@daymade/api-contracts';
import { exhaustiveCheck } from '@daymade/common-lib/types';
import { ClientInferResponseBody } from '@ts-rest/core';
import { reportJinglePlayed } from 'api/analytics';
import { Prompt } from 'components/account/CreateAccountPopup';
import MainLayout from 'components/layout/MainLayout';
import PaddedMaxWidthContainer from 'components/layout/PaddedMaxWidthContainer';
import ALittleLostSection from 'components/token-town/a-little-lost-section';
import ArcadeColourPageSection from 'components/token-town/ArcadeColourPageSection';
import GraphicWrapper from 'components/token-town/GraphicWrapper';
import SuggestionBoxSection from 'components/token-town/suggestion-box-section';
import { TokenTownBalance } from 'components/token-town/TokenTownBalance';
import { TokenTownRedemption } from 'components/token-town/TokenTownRedemption';
import { TokenTownTabs } from 'components/token-town/TokenTownTabs';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useUserStatus } from 'hooks/useUserStatus';
import { observer } from 'mobx-react-lite';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import Router, { useRouter } from 'next/router';
import React, { Suspense, useEffect, useReducer, useState } from 'react';
import { tokenApiClient } from 'utils/apiClient';
import { ASSET_PREFIX } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';

const CreateAccountPopup = React.lazy(
  () => import('components/account/CreateAccountPopup'),
);

type TCreateAccountPopupAccountInfo = {
  userId: string;
  emailAddress: string;
};

type TTokenTownState = {
  createAccountPopupAccountInfo: TCreateAccountPopupAccountInfo | null;
};

type TTokenTownAction = {
  type: 'setCreateAccountPopupAccountInfo';
  payload: TCreateAccountPopupAccountInfo | null;
};

const initialState: TTokenTownState = {
  createAccountPopupAccountInfo: null,
};

function reducer(state: TTokenTownState, action: TTokenTownAction) {
  switch (action.type) {
    case 'setCreateAccountPopupAccountInfo':
      return { ...state, createAccountPopupAccountInfo: action.payload };
    default:
      exhaustiveCheck(action.type);
      return state;
  }
}

export type TokenTab = 'games' | 'prizes';

export type TTokenRedemptionOptionsResponse = ClientInferResponseBody<
  typeof tokenContract.public.getTokenRedemptionOptions,
  200
>;

export type TTokenRedemptionOptionsItem =
  TTokenRedemptionOptionsResponse[number];

interface ITokenTown {
  gameRedemptionOptions: TTokenRedemptionOptionsResponse;
  prizeRedemptionOptions: TTokenRedemptionOptionsResponse;
}

const TokenTown = observer(
  ({ gameRedemptionOptions, prizeRedemptionOptions }: ITokenTown) => {
    const router = useRouter();
    const { currentUser, balances, updateTokenBalance } = useUserStore();
    const [state, dispatch] = useReducer(reducer, initialState);
    // Read tab from URL query param, default to 'games'
    const tabFromQuery = (router.query.tab as TokenTab) || 'games';
    const [activeTokenTab, setActiveTokenTab] =
      useState<TokenTab>(tabFromQuery);
    const { emailAddress, userId, action } = router.query;

    const redeemingTokens = action === 'redeem-tokens';
    // When tab changes, update URL query param
    useEffect(() => {
      // Only update if not already correct
      if (router.query.tab !== activeTokenTab) {
        router.replace(
          {
            pathname: router.pathname,
            query: { ...router.query, tab: activeTokenTab },
          },
          undefined,
          { shallow: true },
        );
      }
    }, [activeTokenTab]);

    // When URL changes (e.g. back/forward), update tab state
    useEffect(() => {
      const urlTab = (router.query.tab as TokenTab) || 'games';
      if (urlTab !== activeTokenTab) {
        setActiveTokenTab(urlTab);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.query.tab]);

    const headerTabs = [
      {
        name: 'games',
        action: () => setActiveTokenTab('games'),
      },
      {
        name: 'prizes',
        action: () => setActiveTokenTab('prizes'),
      },
    ];

    const { loggedIn, guestPlayer } = useUserStatus(
      currentUser,
      emailAddress,
      userId,
    );

    const tokenBalance = balances?.tokenBalance || 0;

    useEffect(() => {
      if (redeemingTokens && !currentUser) {
        handleAttemptToRedeemTokensWhileLoggedOut();
      }

      if (currentUser) {
        updateTokenBalance();
      }
    }, []);

    const handleAttemptToRedeemTokensWhileLoggedOut = () => {
      if (guestPlayer) {
        dispatch({
          type: 'setCreateAccountPopupAccountInfo',
          payload: {
            userId: userId as string,
            emailAddress: emailAddress as string,
          },
        });
      } else {
        Router.push('/login?redirect=token-town');
      }
    };

    const getOnClickAction = (
      redemptionOption: TTokenRedemptionOptionsItem,
    ) => {
      if (!loggedIn) {
        return handleAttemptToRedeemTokensWhileLoggedOut();
      }

      return Router.push(`/token-town/${redemptionOption.slug}`);
    };

    const playJingle = () => {
      (
        document.getElementById('token-town-jingle') as HTMLAudioElement
      ).play?.();
      reportJinglePlayed();
    };

    return (
      <MainLayout>
        <NextSeo
          title="Token Town | Win Epic Prizes | DAYMADE"
          description="Enter holiday competitions, tech raffles and beauty giveaways in our epic Token Town. Win amazing prizes for the price of a coffee!"
          openGraph={{
            title: 'Token Town | Win Epic Prizes | DAYMADE',
            description:
              'Enter holiday competitions, tech raffles and beauty giveaways in our epic Token Town. Win amazing prizes for the price of a coffee!',
          }}
        />

        <div className="tokenTown fullWidth flexCentered">
          {state.createAccountPopupAccountInfo && (
            <Suspense fallback={<div />}>
              <CreateAccountPopup
                prompt={Prompt.INTRO_BLOCKING_PRIZE_REDEMPTION}
                userId={state.createAccountPopupAccountInfo.userId}
                emailAddress={state.createAccountPopupAccountInfo.emailAddress}
                onClose={() =>
                  dispatch({
                    type: 'setCreateAccountPopupAccountInfo',
                    payload: null,
                  })
                }
              />
            </Suspense>
          )}

          <GraphicWrapper
            imageDirectory="token-town"
            imageFname="cityscape"
            imagePosition="top"
            imageOverlay={true}
          >
            {loggedIn && currentUser && (
              <TokenTownBalance tokenBalance={tokenBalance} />
            )}
            <h1 className="header" onClick={() => playJingle()}>
              Welcome to
              <br />
              <strong>Token Town</strong>
              <audio
                id="token-town-jingle"
                src={`${ASSET_PREFIX}/audio/token-town-jingle.m4a`}
              />
            </h1>

            <p className="subheading">
              Redeem the tokens you’ve collected for prizes or more ways to win
            </p>
          </GraphicWrapper>

          <ArcadeColourPageSection additionalClassName="tokenTownTabsSection">
            <TokenTownTabs tabs={headerTabs} activeTab={activeTokenTab} />
          </ArcadeColourPageSection>

          {activeTokenTab === 'games' && (
            <PaddedMaxWidthContainer additionalClassName="redemptionOptions">
              <div className="redemptionWrapper">
                <img
                  src={`${ASSET_PREFIX}/images/token-town/game-console.svg`}
                  alt="game-console"
                />
                <h2>Tokens for Games</h2>
                <p>Even more ways to win</p>
                <TokenTownRedemption
                  redemptionOptions={gameRedemptionOptions}
                  tokenBalance={tokenBalance}
                  handleRedemptionSelect={getOnClickAction}
                  redemptionType={activeTokenTab}
                />
              </div>
            </PaddedMaxWidthContainer>
          )}

          {activeTokenTab === 'prizes' && (
            <PaddedMaxWidthContainer additionalClassName="redemptionOptions">
              <div className="redemptionWrapper">
                <img
                  src={`${ASSET_PREFIX}/images/token-town/arcade-checkout-icon.svg`}
                  alt="checkout-icon"
                />
                <h2>Tokens for Prizes</h2>
                <p>Redeem for prizes and more!</p>
                <TokenTownRedemption
                  redemptionOptions={prizeRedemptionOptions}
                  tokenBalance={tokenBalance}
                  handleRedemptionSelect={getOnClickAction}
                  redemptionType={activeTokenTab}
                />
              </div>
            </PaddedMaxWidthContainer>
          )}

          <ALittleLostSection />

          <SuggestionBoxSection />

          <style jsx>
            {`
              .tokenTown {
                text-align: center;
              }

              :global(.redemptionOptions) {
                padding-top: 0;
                padding-bottom: 2em;
              }

              .faqSection p.faqTitle {
                text-transform: none;
                font-weight: 700;
                letter-spacing: 0;
                text-align: left;
              }

              :global(.tokenTown .purchaseTokensBannerSmall) {
                display: flex;
              }

              :global(.tokenTown .tokenTownBalance) {
                align-self: flex-end;
              }

              .header {
                padding-top: ${loggedIn && currentUser ? `5em` : `10em`};
              }

              .subheading {
                max-wdith: 575px;
                font-size: 1.25em;
              }

              :global(.tokenTownTabsSection) {
                padding: 3em 0 2em;
              }

              .purchaseTokensButton {
                font-size: 1em;
                text-decoration: underline;
                cursor: pointer;
              }

              .redemptionWrapper {
                padding-top: 3em;
                width: 100%;
              }

              .redemptionWrapper img {
                width: 34px;
                height: 34px;
              }

              .redemptionWrapper h2 {
                font-size: 1.2em;
                margin-top: 1em;
              }

              @media (min-width: 1000px) {
                .subheading {
                  font-size: 2em;
                }
              }

              @media (min-width: 700px) {
                .purchaseTokensButton {
                  font-size: 1.5em;
                }

                .redemptionWrapper {
                  padding-top: 6em;
                }

                .redemptionWrapper img {
                  width: 54px;
                  height: 54px;
                }

                .redemptionWrapper h2 {
                  font-size: 2.3em;
                  margin-top: 1em;
                }

                .redemptionWrapper p {
                  font-size: 2em;
                  margin-bottom: 2em;
                }
              }
            `}
          </style>
        </div>
      </MainLayout>
    );
  },
);

export const getServerSideProps: GetServerSideProps<ITokenTown> = async () => {
  let gameRedemptionOptions: TTokenRedemptionOptionsResponse = [];
  let prizeRedemptionOptions: TTokenRedemptionOptionsResponse = [];

  const result = await tokenApiClient.public.getTokenRedemptionOptions();

  // workaround nextjs serialization issue
  const allOptions: TTokenRedemptionOptionsResponse = JSON.parse(
    JSON.stringify(result.body),
  );

  if (result.status === HttpStatusCode.OK) {
    gameRedemptionOptions = allOptions.filter(
      (option) => option.group === 'GAME',
    );
    prizeRedemptionOptions = allOptions.filter(
      (option) => option.group === 'PRIZE',
    );
  }

  return {
    props: {
      gameRedemptionOptions,
      prizeRedemptionOptions,
      revalidate: 3600,
    },
  };
};

export default TokenTown;
